import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, Link,
  TextField,
  Box,
  MenuItem, Typography,
  Autocomplete,
  Chip,
} from "@mui/material";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { Authenticated } from "ra-core";
import { DatePicker } from "@mui/x-date-pickers";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { CookService } from "../../services/CookServices";
import { maskPhoneNumber } from "../../utils/PhoneNumberUtils";
import '../OmsTimeTable/index.css';
import { OneMealService } from "../../services/OneMealService";
import { TrialsService } from "../../services/TrialServices";
import UserBundleService from "../../services/BundleService";
import { OneMeal } from "../../interfaces/OneMeal/OneMeal";
import { OneMealServiceStatus } from "../../enums/OneMealServiceStatus";
import CityService from "../../services/CityService";
import CityResponse from "../../interfaces/City/CityResponse";
import AreaResponse from "../../interfaces/Area/AreaResponse";
import AreaService from "../../services/AreaService";
import { ServiceOfferingEnums } from "../../enums/ServiceOfferingEnums";
import { CookReferenceModelName } from "../../enums/CookReferenceModelName";
import { TrialsV3Status } from "../../enums/TrialsV3Status";
import { debounce } from "lodash";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import CircularLoader from "../Common/CircularLoader";
import ChefForPartStatusEnum from "../../enums/ChefForPartStatusEnum";
import ChefForPartyService from "../../services/ChefForPartyService";
import { CookServiceTypeEnum, CookServiceTypeLabels } from "../../enums/CookServiceTypeEnum";
import { CookTypeFilter } from "../../enums/CookTypeFilterEnum";

//Common styles
import {
  FilterContainer,
  FilterSection,
  FilterTextField,
  FilterWrapper,
  FIlterHeader,
  SectionHeader,
  SectionTitle,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";
import { FilterIcon } from "../Svg";

// Mirrors the FilterTextField look for the plain TextFields used inside
// Autocompletes (the styled component's resets break the Autocomplete popup).
// The doubled && selector outranks Autocomplete's own slot styles.
const autocompleteInputSx = {
  "&& .MuiFormHelperText-root": {
    display: "none",
  },
  "&& .MuiInputBase-root": {
    borderRadius: "8px",
    backgroundColor: "rgb(249, 250, 251)",
  },
  "&& .MuiOutlinedInput-root fieldset": {
    borderColor: "rgb(225, 231, 239)",
  },
  "&& .MuiOutlinedInput-root:hover fieldset": {
    borderColor: "rgb(225, 231, 239)",
  },
  "&& .MuiOutlinedInput-root.Mui-focused fieldset": {
    borderColor: "rgb(225, 231, 239)",
    borderWidth: "1px",
  },
};

moment.locale("en-GB");
const localizer = momentLocalizer(moment);

const cookService = new CookService();
const oneMealService = new OneMealService();
const trialsService = new TrialsService();
const userBundleService = new UserBundleService();
const cityService = new CityService();
const areaService = new AreaService();
const chefForPartyService = new ChefForPartyService();

// ── Filter persistence ────────────────────────────────────────────────────────
const FILTER_STORAGE_KEY = 'cookCalendarAppliedFilters.v2';

interface CalendarFilters {
  date: string; // ISO string
  city: any;
  areaId: number | null;
  area: any;
  serviceId: string[];
  cookType: string;
  cookTypeList: string[];
  cookIds: number[];
  selectedCooks: { id: number; label: string }[];
}

const DEFAULT_FILTERS: CalendarFilters = {
  date: moment().format(),
  city: null,
  areaId: null,
  area: null,
  serviceId: [],
  cookType: '',
  cookTypeList: [],
  cookIds: [],
  selectedCooks: [],
};

function loadPersistedFilters(): CalendarFilters {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) return { ...DEFAULT_FILTERS, ...JSON.parse(stored) };
  } catch {}
  // fall back to omsTimeTableDateFilter for date
  const storedDate = localStorage.getItem('omsTimeTableDateFilter');
  return storedDate ? { ...DEFAULT_FILTERS, date: moment(storedDate).format() } : DEFAULT_FILTERS;
}

function saveFilters(filters: CalendarFilters) {
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
}
// ─────────────────────────────────────────────────────────────────────────────

const legends = [
  { color: 'darkgrey', label: 'BLOCKED' },
  { color: '#43a047', label: 'SERVICES FULFILLED' },
  { color: '#1d89e4', label: 'COOK_ALLOCATED, COOK_CONFIRMED before SERVICES Start time' },
  { color: '#e53935', label: 'COOK_ALLOCATED, COOK_CONFIRMED after SERVICES Start time' },
  { color: 'black', label: 'PENDING SERVICES' },
  { color: '#FF7722', label: 'PARTY' },
  { color: '#dcfd35 ', label: 'ADMIN_BLOCKED' },
  { color: '#FFA726', label: 'LUNCH' },
  { color: '#b39ddb', label: '4TH_VISIT_BLOCKED' },
  { color: 'pink', label: 'LEAVE' },
];

const CustomToolbar = (props: any) => (
  <div className="rbc-toolbar">
    <span className="rbc-toolbar-label">{props?.label}</span>
  </div>
);

const CookServeCalendar = (props: any) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [omsLoading, setOmsLoading] = useState<boolean>(true);
  const [trailLoading, setTrialLoading] = useState<boolean>(true);
  const [partyLoading, setPartyLoading] = useState<boolean>(true);

  const [dialog, setDialog] = useState<boolean>(false);
  const [blockedDialog, setBlockedDialog] = useState<boolean>(false);
  const [details, setDetails] = useState<any>();
  const [cookOmsMapping, setCookOmsMapping] = useState<any>({});

  const [lunchDialog, setLunchDialog] = useState<boolean>(false);
  const [lunchDetails, setLunchDetails] = useState<any>(null);
  const [lunchConfirmDialog, setLunchConfirmDialog] = useState<boolean>(false);
  const [lunchRemoveReason, setLunchRemoveReason] = useState<string>('');
  const [lunchRemoveLoading, setLunchRemoveLoading] = useState<boolean>(false);

  const [cityList, setCityList] = useState<CityResponse[]>([]);
  const [allAreaList, setAllAreaList] = useState<AreaResponse[]>([]);
  const [areaList, setAreaList] = useState<AreaResponse[]>([]);
  const [areaInput, setAreaInput] = useState<string>('');

  // Cook search for multi-cook filter
  const [cookSearchInput, setCookSearchInput] = useState<string>('');
  const [cookSearchResults, setCookSearchResults] = useState<{ id: number; label: string }[]>([]);

  // Pending filters (UI state — changes as user interacts, doesn't trigger API)
  const [pending, setPending] = useState<CalendarFilters>(loadPersistedFilters);

  // Applied filters (drives API calls — only changes on Apply / Reset)
  const [applied, setApplied] = useState<CalendarFilters>(loadPersistedFilters);

  const isAnyLoading = loading || omsLoading || trailLoading || partyLoading;

  // ── Event helpers ─────────────────────────────────────────────────────────
  const eventStyleGetter = (event: any) => {
    const backgroundColor = event.color || '#3174ad';
    const color = ['#e53935', '#1d89e4', 'black'].includes(event.color) ? 'white' : 'black';
    return { style: { backgroundColor, color, border: '1px solid white' } };
  };

  function mergeContinuousTimeSlots(timeSlots: any[]) {
    if (!timeSlots.length) return [];
    timeSlots.sort((a: { start: Date }, b: { start: Date }) => +new Date(a.start) - +new Date(b.start));
    const mergedSlots = [];
    let currentSlot = timeSlots[0];
    for (let i = 1; i < timeSlots.length; i++) {
      const currentEnd = moment(currentSlot.end);
      const nextStart = moment(timeSlots[i].start);
      if (currentEnd.isSameOrAfter(nextStart) && currentSlot.summary === timeSlots[i].summary) {
        currentSlot.end = timeSlots[i].end;
      } else {
        currentSlot.start = moment(currentSlot.start).add(0.001, 'hours').toDate();
        mergedSlots.push(currentSlot);
        currentSlot = timeSlots[i];
      }
    }
    currentSlot.end = moment(currentSlot.end).subtract(1, 'minute').toDate();
    mergedSlots.push(currentSlot);
    return mergedSlots;
  }

  const isLunchSlot = (comment?: string) => comment?.trim().includes('Auto blocked for lunch');
  const isFourthVisitSlot = (comment?: string) => comment?.trim().includes('Auto blocked to prevent 4th visit');

  const getTitle = useCallback((eachSlot: any) => {
    if (eachSlot?.referenceModelName) return `${eachSlot.referenceId}_${eachSlot.referenceModelName}`;
    if (eachSlot?.status === 'CK_BLOCKED') {
      const comment = eachSlot?.adminComments?.trim();
      if (isLunchSlot(comment)) return 'LUNCH';
      return isFourthVisitSlot(comment) ? '4TH_VISIT_BLOCKED' : 'ADMIN_BLOCKED';
    }
    if (eachSlot?.cookLeaveId || eachSlot?.leaveMarked !== null) return 'LEAVE';
    if (eachSlot?.status === 'BLOCKED') return 'BLOCKED';
    return 'PENDING';
  }, []);

  const getColor = useCallback((eachSlot: any) => {
    if ([CookReferenceModelName.ONE_MEAL_SERVICE, CookReferenceModelName.COOK_SUBSCRIPTION_V3, CookReferenceModelName.TRIALS_V3].includes(eachSlot?.referenceModelName)) {
      const now = moment();
      const slotDateTime = moment(moment(eachSlot.date).format('YYYY-MM-DD') + 'T' + moment(eachSlot.timeSlot, 'h:mm A').format('HH:mm'));
      if (eachSlot?.serviceStatus === OneMealServiceStatus.FULFILLED) return '#43a047';
      if ([OneMealServiceStatus.COOK_ALLOCATED, OneMealServiceStatus.COOK_CONFIRMED].includes(eachSlot?.serviceStatus) && !now.isAfter(slotDateTime)) return '#1d89e4';
      if ([OneMealServiceStatus.COOK_ALLOCATED, OneMealServiceStatus.COOK_CONFIRMED].includes(eachSlot?.serviceStatus) && now.isAfter(slotDateTime) && eachSlot?.checkInTime === null) return '#e53935';
      if (eachSlot?.serviceStatus === OneMealServiceStatus.ONGOING && eachSlot?.checkOutTime === null) return '#e53935';
      return '#fdd734';
    }
    if (eachSlot?.referenceModelName === CookReferenceModelName.CHEF_FOR_PARTY) return '#FF7722';
    if (eachSlot?.status === 'CK_BLOCKED' && eachSlot?.referenceModelName === null) {
      if (isLunchSlot(eachSlot?.adminComments)) return '#FFA726';
      return isFourthVisitSlot(eachSlot?.adminComments) ? '#b39ddb' : '#dcfd35';
    }
    if (eachSlot?.cookLeaveId !== null) return 'pink';
    if (eachSlot?.status === 'BLOCKED' && eachSlot?.referenceModelName === null) return '#a9a9a9';
    return 'black';
  }, []);

  // ── Data fetch functions (use `applied` state) ────────────────────────────
  const getAllPendingOms = useCallback(async (start?: Date) => {
    setOmsLoading(true);
    const date = moment(start).format('YYYY-MM-DD');
    const filters: any = { date, paymentStatus: 'PAID', status: TrialsV3Status.PENDING };
    if (applied.city) filters.cityId = applied.city;
    if (applied.areaId) filters.areaId = applied.areaId;
    const timeSheet: { totalPeople: number; noOfSlots: number }[] = await oneMealService.getOmsCookingTime().catch(() => []);
    const getTime = (totalPeople: number | null) => {
      const d = timeSheet.find((t: any) => t?.totalPeople === totalPeople);
      return d ? d.noOfSlots * 0.5 : 1.5;
    };
    oneMealService.getAllOneMealService(1000, 0, filters, {}).then((res) => {
      const uniqueCook = new Set();
      const tempMap: any = {};
      res.data.forEach((obj: OneMeal) => {
        if (!['CANCELLED', 'RESCHEDULED'].includes(obj.status)) {
          const req: any = { summary: `${obj?.id}_ONE_MEAL_SERVICE`, id: obj?.id, referenceModelName: 'ONE_MEAL_SERVICE', referenceId: obj?.id, isLeave: false, cookDetails: null };
          req.omsDetails = obj;
          uniqueCook.add(obj.cook && obj?.cook.id);
          req.startAt = moment(moment(obj.date).format('YYYY-MM-DD') + 'T' + moment(obj.time, 'h:mm A').format('HH:mm')).format();
          req.start = moment(`${obj.date}T${moment(obj.time, 'h:mm A').format('HH:mm')}`).toDate();
          req.end = moment(req.start).add(getTime(obj?.totalPeople), 'hours').toDate();
          req.endAt = moment(req.startAt).add(getTime(obj?.totalPeople), 'hours').format();
          req.title = req.summary;
          req.color = 'black';
          if (obj.cook === null) {
            if (tempMap[0]) tempMap[0].push(req); else tempMap[0] = [req];
          }
        }
      });
      if (Object.keys(tempMap).length) {
        setCookOmsMapping((prev: any) => ({ ...prev, 0: prev?.[0] ? [...prev[0], ...tempMap[0]] : [...tempMap[0]] }));
      }
      setOmsLoading(false);
    }).catch(() => setOmsLoading(false));
  }, [applied]);

  const getAllPendingTrials = useCallback(async (start?: Date) => {
    setTrialLoading(true);
    const date = moment(start).format('YYYY-MM-DD');
    const filters: any = { date, paymentStatus: 'PAID', status: TrialsV3Status.PENDING };
    if (applied.city) filters.cityId = applied.city;
    if (applied.areaId) filters.areaId = applied.areaId;
    trialsService.getAllTrials(1000, 0, filters, {}).then((res) => {
      const uniqueCook = new Set();
      const tempMap: any = {};
      res.data.forEach((obj: any) => {
        if (!['CANCELLED', 'RESCHEDULED'].includes(obj.status)) {
          const req: any = { summary: `${obj?.id}_TRIALS_V3`, id: obj?.id, referenceModelName: 'TRIALS_V3', referenceId: obj?.id, isLeave: false, cookDetails: null };
          req.trialDetails = obj;
          uniqueCook.add(obj.cook && obj?.cook.id);
          req.startAt = moment(moment(obj.date).format('YYYY-MM-DD') + 'T' + moment(obj.time, 'h:mm A').format('HH:mm')).format();
          req.start = moment(`${obj.date}T${moment(obj.timeSlot, 'h:mm A').format('HH:mm')}`).toDate();
          req.end = moment(req.start).add(1.5, 'hours').toDate();
          req.endAt = moment(req.startAt).add(1.5, 'hours').format();
          req.title = req.summary;
          req.color = 'black';
          if (obj.cook === null) {
            if (tempMap[0]) tempMap[0].push(req); else tempMap[0] = [req];
          }
        }
      });
      if (Object.keys(tempMap).length) {
        setCookOmsMapping((prev: any) => ({ ...prev, 0: prev?.[0] ? [...prev[0], ...tempMap[0]] : [...tempMap[0]] }));
      }
      setTrialLoading(false);
    }).catch(() => setTrialLoading(false));
  }, [applied]);

  const getAllPendingParties = useCallback(async (start?: Date) => {
    setPartyLoading(true);
    const date = moment(start).format('YYYY-MM-DD');
    const filters: any = { date, tokenAmountPaymentStatus: 'PAID', remainingAmountPaymentStatus: 'PAID', status: ChefForPartStatusEnum.PENDING };
    if (applied.city) filters.cityId = applied.city;
    if (applied.areaId) filters.areaId = applied.areaId;
    chefForPartyService.fetchAllChefForPart(1000, 0, filters, {}).then((res) => {
      const tempMap: any = {};
      res.data.forEach((obj: any) => {
        if (!['CANCELLED', 'RESCHEDULED'].includes(obj.status)) {
          const req: any = { summary: `${obj?.id}_PARTY`, id: obj?.id, referenceModelName: 'CHEF_FOR_PARTY', referenceId: obj?.id, isLeave: false, cookDetails: null };
          req.partyDetails = obj;
          req.startAt = moment(moment(obj.date).format('YYYY-MM-DD') + 'T' + moment(obj.time, 'h:mm A').format('HH:mm')).format();
          req.start = moment(`${obj.date}T${moment(obj.time, 'h:mm A').format('HH:mm')}`).toDate();
          req.end = moment(req.start).add(3, 'hours').toDate();
          req.endAt = moment(req.startAt).add(3, 'hours').format();
          req.title = req.summary;
          req.color = '#FF7722';
          if (obj.cooks.length === 0) {
            if (tempMap[0]) tempMap[0].push(req); else tempMap[0] = [req];
          }
        }
      });
      if (Object.keys(tempMap).length) {
        setCookOmsMapping((prev: any) => ({ ...prev, 0: prev?.[0] ? [...prev[0], ...tempMap[0]] : [...tempMap[0]] }));
      }
      setPartyLoading(false);
    }).catch(() => setPartyLoading(false));
  }, [applied]);

  const getAllCooks = useCallback(() => {
    setLoading(true);
    const payload: any = { date: moment(applied.date).format('YYYY-MM-DD') };
    if (applied.city) payload.cityId = applied.city;
    if (applied.areaId) payload.areaId = applied.areaId;
    if (applied.serviceId?.length) payload.serviceId = applied.serviceId;
    if (applied.cookType) payload.cookType = applied.cookType;
    if (applied.cookTypeList?.length) payload.cookTypeList = applied.cookTypeList;
    if (applied.cookIds?.length) payload.cookIds = applied.cookIds;

    cookService.getAllcookServeCalendar(payload).then((res) => {
      const uniqueCook = new Set();
      const tempMap: any = {};
      let tempDetails: any = {};
      [...res].forEach((obj: any) => {
        const timeSlots: any[] = [];
        obj?.calender?.forEach((eachSlot: any) => {
          const req: any = {
            summary: '', id: NaN, startAt: '', endAt: '', color: '', heading: '',
            cookId: '', referenceId: '', referenceModelName: '', isLeave: false,
            cookDetails: {}, lastUpdatedBy: '', lastUpdatedByType: '', adminComments: '',
          };
          req.heading = `${obj?.cook?.firstName} ${obj?.cook?.lastName}`.length < 15
            ? `${obj?.cook?.firstName} ${obj?.cook?.lastName}`
            : `${obj?.cook?.firstName} ${obj?.cook?.lastName}`.substring(0, 15) + '...';
          req.cookId = obj?.cook?.cookId;
          req.summary = getTitle(eachSlot);
          req.referenceId = eachSlot?.referenceId;
          req.referenceModelName = eachSlot?.referenceModelName;
          req.cookDetails = obj?.cook;
          uniqueCook.add(obj?.cook?.cookId);
          req.id = eachSlot?.id;
          req.startAt = moment(eachSlot.date + 'T' + moment(eachSlot.timeSlot, 'h:mm A').format('HH:mm')).format();
          req.start = moment(`${eachSlot.date}T${moment(eachSlot.timeSlot, 'h:mm A').format('HH:mm')}`).toDate();
          req.end = moment(req.start).add(0.5, 'hours').toDate();
          req.endAt = moment(req.startAt).add(0.5, 'hours').format();
          req.title = getTitle(eachSlot);
          req.color = getColor(eachSlot);
          req.lastUpdatedBy = eachSlot?.lastUpdatedBy;
          req.lastUpdatedByType = eachSlot?.lastUpdatedByType;
          req.adminComments = eachSlot?.adminComments;
          req.cookLeaveId = eachSlot?.cookLeaveId;
          tempDetails = { ...req };
          if (eachSlot?.leaveMarked !== null || eachSlot?.cookLeaveId !== null || (eachSlot?.status === 'CK_BLOCKED' && eachSlot?.referenceModelName === null) || (eachSlot?.status === 'BLOCKED' && eachSlot?.referenceModelName === null)) {
            req.isLeave = true;
          }
          if (eachSlot?.referenceModelName !== null || eachSlot?.leaveMarked !== null || eachSlot?.cookLeaveId !== null || eachSlot?.status === 'CK_BLOCKED' || eachSlot?.status === 'BLOCKED') {
            timeSlots.push(req);
          }
        });
        let mergedSlots = mergeContinuousTimeSlots(timeSlots);
        if (mergedSlots.length === 0) {
          tempDetails.start = '';
          tempDetails.end = '';
          mergedSlots = [tempDetails];
        }
        if (obj.cookId !== null) {
          if (tempMap[obj?.cook?.cookId]) tempMap[obj?.cook?.cookId].push(...mergedSlots);
          else tempMap[obj?.cook?.cookId] = [...mergedSlots];
        }
      });
      setCookOmsMapping((prev: any) => ({ ...prev, ...tempMap }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [applied, getColor, getTitle]);

  // ── Apply / Reset ─────────────────────────────────────────────────────────
  const applyFilters = () => {
    saveFilters(pending);
    setApplied({ ...pending });
    setCookOmsMapping({});
  };

  const resetFilters = () => {
    const fresh = { ...DEFAULT_FILTERS, date: moment().format() };
    setPending(fresh);
    setApplied(fresh);
    saveFilters(fresh);
    setCookOmsMapping({});
    setAreaInput('');
    setCookSearchInput('');
  };

  // ── Pending filter helpers ────────────────────────────────────────────────
  const setPendingField = (field: keyof CalendarFilters, value: any) =>
    setPending(prev => ({ ...prev, [field]: value }));

  // ── Cook search (debounced) ───────────────────────────────────────────────
  useEffect(() => {
    const debouncedSearch = debounce(async () => {
      if (!cookSearchInput || cookSearchInput.trim().length < 2) {
        setCookSearchResults([]);
        return;
      }
      const results = await cookService.searchCooksForCalendar(cookSearchInput).catch(() => []);
      setCookSearchResults(results);
    }, 400);
    debouncedSearch();
    return () => debouncedSearch.cancel();
  }, [cookSearchInput]);

  // ── Area list (debounced by city + input) ─────────────────────────────────
  useEffect(() => {
    const debouncedFetchAreas = debounce(() => {
      if (!pending.city && !areaInput) {
        setAreaList(allAreaList);
        return;
      }
      setAreaList([]);
      const f: Record<string, number | string> = {};
      if (pending.city) f.cityId = pending.city;
      if (areaInput) f.name = areaInput;
      areaService.fetchAllAreas(1000, 0, f, { field: 'name', order: 'ASC' }).then((res) => setAreaList(res?.data));
    }, 500);
    debouncedFetchAreas();
    return () => debouncedFetchAreas.cancel();
  }, [pending.city, areaInput, allAreaList]);

  // ── Fetch city + area lists on mount ──────────────────────────────────────
  useEffect(() => {
    cityService.fetchAllCities(1000, 0, { isCurrentlyServing: 'YES' }, {}).then(res => setCityList(res?.data));
    areaService.fetchAllAreas(1000, 0, {}, { field: 'name', order: 'ASC' }).then(res => {
      setAllAreaList(res?.data);
      setAreaList(res?.data);
    });
  }, []);

  // ── Trigger data fetch when applied filters change ────────────────────────
  useEffect(() => {
    const dateObj = moment(applied.date).toDate();
    setCookOmsMapping({});
    getAllCooks();
    getAllPendingTrials(dateObj);
    getAllPendingOms(dateObj);
    getAllPendingParties(dateObj);
    const interval = setInterval(() => {
      getAllCooks();
      getAllPendingTrials(dateObj);
      getAllPendingOms(dateObj);
      getAllPendingParties(dateObj);
    }, 1000 * 60 * 5);
    return () => clearInterval(interval);
  }, [applied, getAllCooks, getAllPendingOms, getAllPendingTrials, getAllPendingParties]);

  // ── Sync scroll across calendars ──────────────────────────────────────────
  const calendarRefs = useRef<any>({});
  const syncScroll = (scrollTop: any, sourceKey: any) => {
    Object.entries(calendarRefs.current).forEach(([key, ref]: any) => {
      if (key !== Number(sourceKey) && ref) {
        const div = ref.querySelector('.rbc-time-content');
        if (div && div.scrollTop !== scrollTop) div.scrollTop = scrollTop;
      }
    });
  };
  useEffect(() => {
    const listeners: any[] = [];
    Object.entries(calendarRefs.current).forEach(([key, ref]: any) => {
      if (ref) {
        const div = ref.querySelector('.rbc-time-content');
        if (div) {
          const handleScroll = () => syncScroll(div.scrollTop, key);
          div.addEventListener('scroll', handleScroll);
          listeners.push({ div, handleScroll });
        }
      }
    });
    return () => listeners.forEach(({ div, handleScroll }) => div.removeEventListener('scroll', handleScroll));
  }, [cookOmsMapping]);

  // ── Dialog components ─────────────────────────────────────────────────────
  const DataContainer = ({ title = '', value = '---' }: { title: string; value: string }) => (
    <div>
      <b style={{ minWidth: '170px', display: 'inline-block' }}>{title}</b>
      <span>{value}</span>
    </div>
  );

  const DialogDetails = (props: any) => {
    const cook = props?.data?.cookDetails;
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [redirectLink, setRedirectLink] = useState('');
    useEffect(() => {
      setLoading(true);
      if (props?.data?.referenceModelName === 'ONE_MEAL_SERVICE') {
        setRedirectLink(`/#/OneMealService/${props?.data?.referenceId}/show`);
        oneMealService.getOneMealService(props?.data?.referenceId).then((res: any) => { setData(res?.data); setLoading(false); });
      } else if (props?.data?.referenceModelName === 'TRIALS_V3') {
        setRedirectLink(`/#/Trials/${props?.data?.referenceId}/show`);
        trialsService.getTrialsById(props?.data?.referenceId).then((res: any) => { setData(res?.data); setLoading(false); });
      } else if (props?.data?.referenceModelName === 'COOK_SUBSCRIPTION_V3') {
        cookService.getSubscriptionForACook(props?.data?.referenceId).then((d: any) => {
          setRedirectLink(`/#/UserBundles/${d?.userBundleMappingId}/show/7`);
          userBundleService.fetchOneUserBundleMapping(d?.userBundleMappingId).then((res: any) => { setData(res?.data); setLoading(false); });
        });
      } else if (props?.data?.referenceModelName === 'CHEF_FOR_PARTY') {
        setRedirectLink(`/#/ChefForParty/${props?.data?.referenceId}/show`);
        chefForPartyService.fetchOneChefForParty(props?.data?.referenceId).then((res: any) => { setData(res?.data); setLoading(false); });
      }
    }, [props?.data?.referenceId, props?.data?.referenceModelName]);
    return (
      <Dialog onClose={props.handleClose} open={props.open} scroll="paper" maxWidth="xl">
        <DialogTitle>
          <b>{props?.data?.referenceModelName}</b>
          <Button variant="contained" sx={{ borderRadius: 30, fontSize: '10px', ml: 2 }} size="small" component={Link} href={redirectLink}>View Full Details</Button>
        </DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', width: '400px' }}>
          {!loading && props?.data?.referenceModelName === 'ONE_MEAL_SERVICE' && data?.oneMealServiceAmount !== 0 && data?.oneMealServiceAmount === data?.discount && (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: '4px', backgroundColor: '#ffca69', borderRadius: '12px', mb: 1 }}>
              <Typography fontWeight="600">Pack Visit: 100% Coupon Applied</Typography>
            </Box>
          )}
          <DataContainer title="Status:" value={data?.status} />
          {cook && (
            <>
              <DataContainer title="Cook Id:" value={cook?.cookId} />
              <DataContainer title="Cook Name:" value={cook?.firstName + ' ' + cook?.lastName} />
              <DataContainer title="Cook PhoneNo:" value={maskPhoneNumber(data?.cookPhoneNumber)} />
              <DataContainer title="Onboarding Status:" value={cook?.onboardingStatus} />
            </>
          )}
          <DataContainer title="Customer Name:" value={data?.customerName} />
          <DataContainer title="Customer PhoneNo:" value={data?.customerPhoneNumber} />
          <DataContainer title="City:" value={data?.cityName} />
          <DataContainer title="Area:" value={data?.areaName} />
          <DataContainer title="Locality:" value={data?.localityName} />
          <DataContainer title="Customer Address:" value={data?.house?.address + ' ' + data?.house?.locality + ' ' + data?.house?.city} />
          <DataContainer title="Total People:" value={data?.totalPeople} />
          <DataContainer title="Allocator Name:" value={data?.allocatorName} />
          <DataContainer title="Date:" value={moment(data?.date).format('DD MMM YYYY')} />
          <DataContainer title="Time:" value={data?.time} />
          <DataContainer title="Check In Time:" value={data?.checkInTime ? moment(data?.checkInTime).format('DD/MM/YYYY hh:mm A') : 'N/A'} />
          <DataContainer title="Customer Feedback:" value={data?.customerFeedback} />
          <DataContainer title="Check Out Time:" value={data?.checkOutTime ? moment(data?.checkOutTime).format('DD/MM/YYYY hh:mm A') : 'N/A'} />
          <DataContainer title="Customer Comments:" value={data?.customerComments?.[0]?.comment} />
          <DataContainer title="Customer Rating:" value={data?.customerRating} />
        </DialogContent>
        <DialogActions style={{ marginTop: '12px' }}>
          <Button variant="contained" color="error" onClick={() => props.handleClose(false)} size="large">Cancel</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const DialogDetailsBlocked = (props: any) => (
    <Dialog onClose={props.handleClose} open={props.open} scroll="paper" maxWidth="xl">
      <DialogContent style={{ display: 'flex', flexDirection: 'column', width: '400px' }}>
        <Link href={`/#/AdminUsers/${props?.data?.lastUpdatedBy}/show`}>
          <DataContainer title="Blocked By:" value={props?.data?.lastUpdatedBy} />
        </Link>
        <DataContainer title="Blocked By Type:" value={props?.data?.lastUpdatedByType} />
        <DataContainer title="Comments:" value={props?.data?.adminComments} />
      </DialogContent>
      <DialogActions style={{ marginTop: '12px' }}>
        <Button variant="contained" color="error" onClick={() => props.handleClose(false)} size="large">Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  const handleLunchRemove = async () => {
    if (!lunchDetails) return;
    setLunchRemoveLoading(true);
    try {
      const date = moment(lunchDetails.start).format('YYYY-MM-DD');
      await cookService.overrideLunchBreak(Number(lunchDetails.cookId), {
        type: 'DAY_LEVEL',
        date,
        reason: lunchRemoveReason,
      });
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Lunch slot removed successfully',
        type: CustomSnackbarTypes.SUCCESS,
      });
      setLunchConfirmDialog(false);
      setLunchDialog(false);
      setLunchRemoveReason('');
      setCookOmsMapping({});
      getAllCooks();
    } catch (_err) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Failed to remove lunch slot',
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setLunchRemoveLoading(false);
    }
  };

  const onEventClick = (data: any) => {
    if (data?.title === 'LEAVE') {
      const leaveId = data?.cookLeaveId || data?.id;
      if (leaveId) {
        window.location.hash = `/CookLeaves/${leaveId}/show`;
      }
      return;
    }
    if (data?.title === 'LUNCH') {
      setLunchDetails(data);
      setLunchDialog(true);
      return;
    }
    if (data?.referenceId) setDialog(true);
    else if (data?.lastUpdatedBy) setBlockedDialog(true);
    setDetails(data);
  };

  const appliedDate = moment(applied.date).toDate();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Authenticated>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {dialog && <DialogDetails open={dialog} handleClose={() => setDialog(false)} data={details} />}
        {blockedDialog && <DialogDetailsBlocked open={blockedDialog} handleClose={() => setBlockedDialog(false)} data={details} />}

        {/* Lunch slot detail popup */}
        <Dialog open={lunchDialog} onClose={() => setLunchDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>🍽️ Lunch Slot</DialogTitle>
          <DialogContent>
            <Box sx={{ background: '#fff8e1', borderRadius: 2, p: 1.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Date</Typography>
              <Typography fontWeight={600} fontSize="0.9rem">
                {lunchDetails ? moment(lunchDetails.start).format('DD MMM YYYY') : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Timing</Typography>
              <Typography fontWeight={600} fontSize="0.9rem">
                {lunchDetails
                  ? `${moment(lunchDetails.start).format('h:mm A')} – ${moment(lunchDetails.end).format('h:mm A')}`
                  : ''}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setLunchDialog(false)} color="inherit" variant="outlined" size="small">
              Close
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => setLunchConfirmDialog(true)}
            >
              Remove Lunch Slot
            </Button>
          </DialogActions>
        </Dialog>

        {/* Lunch slot removal confirmation */}
        <Dialog open={lunchConfirmDialog} onClose={() => { if (!lunchRemoveLoading) setLunchConfirmDialog(false); }} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Confirm Removal</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Are you sure you want to remove the lunch slot on{' '}
              <strong>{lunchDetails ? moment(lunchDetails.start).format('DD MMM YYYY') : ''}</strong>?
            </Typography>
            <TextField
              label="Reason"
              value={lunchRemoveReason}
              onChange={(e) => setLunchRemoveReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter reason for removal..."
              size="small"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setLunchConfirmDialog(false)}
              disabled={lunchRemoveLoading}
              color="inherit"
              variant="outlined"
              size="small"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={handleLunchRemove}
              disabled={lunchRemoveLoading || !lunchRemoveReason.trim()}
              startIcon={lunchRemoveLoading ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {lunchRemoveLoading ? 'Removing…' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Filter Bar ── */}
        <FilterWrapper style={{ margin: '12px 15px 4px' }}>
          <FIlterHeader>
            <FilterSection style={{ marginBottom: '0' }}>
              <SectionHeader>
                <WorkSpaceTitleRow>
                  <FilterIcon color="#1976d2" height={20} width={20} />
                  <SectionTitle>Filters</SectionTitle>
                </WorkSpaceTitleRow>
              </SectionHeader>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" onClick={applyFilters} sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}>
                  Apply
                </Button>
                <Button variant="outlined" size="small" onClick={resetFilters} color="inherit" sx={{ textTransform: 'none', px: 2, borderColor: '#ccc', color: 'text.secondary' }}>
                  Reset
                </Button>
              </Box>
            </FilterSection>
          </FIlterHeader>

          <FilterContainer style={{ flexWrap: 'wrap', rowGap: '12px' }}>

          {/* City */}
          <FilterTextField
            select
            label="City"
            size="small"
            variant="outlined"
            value={pending.city ?? 0}
            onChange={(e: any) => {
              setPendingField('city', Number(e.target.value) === 0 ? null : e.target.value);
              setPendingField('areaId', null);
              setPendingField('area', null);
              setAreaInput('');
            }}
            sx={{ minWidth: 140 }}
          >
              <MenuItem value={0}>All Cities</MenuItem>
              {cityList.map(c => <MenuItem key={c.name} value={c.id}>{c.name}</MenuItem>)}
          </FilterTextField>

          {/* Area */}
          <Autocomplete
            size="small"
            sx={{ minWidth: 190 }}
            options={areaList.map((a: AreaResponse) => ({ label: a.name, value: a.id }))}
            onChange={(_, data: any) => { setPendingField('areaId', data?.value ?? null); setPendingField('area', data); }}
            value={pending.area}
            renderInput={(params) => <TextField {...params} label="Area" variant="outlined" size="small" sx={autocompleteInputSx} />}
            inputValue={areaInput}
            onInputChange={(_, v) => setAreaInput(v)}
          />

          {/* Date */}
          <Box display="flex" alignItems="center" sx={{ border: '1px solid rgb(225, 231, 239)', borderRadius: '8px', height: '40px', backgroundColor: 'rgb(249, 250, 251)', overflow: 'hidden' }}>
            <Box onClick={() => setPendingField('date', moment(pending.date).subtract(1, 'd').format())}
              sx={{ px: 1, height: '100%', display: 'flex', alignItems: 'center', cursor: 'pointer', '&:hover': { backgroundColor: '#f0f0f0' } }}>
              <ArrowBackIosIcon sx={{ fontSize: '14px', color: 'rgba(0,0,0,0.54)' }} />
            </Box>
            <DatePicker
              mask=""
              label=""
              inputFormat="dd MMM yyyy"
              value={moment(pending.date).toDate()}
              onChange={(v: any) => v && setPendingField('date', moment(v).format())}
              renderInput={({ inputRef, inputProps, InputProps }) => (
                <Box display="flex" alignItems="center" sx={{ gap: 0.5, px: 0.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }} ref={inputRef}>
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1, mb: '1px' }}>
                      {moment(pending.date).format('ddd')}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
                      {moment(pending.date).format('DD MMM YYYY')}
                    </Typography>
                  </Box>
                  <input {...inputProps} style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }} />
                  <Box sx={{ color: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    {InputProps?.endAdornment}
                  </Box>
                </Box>
              )}
              onAccept={(v: any) => v && localStorage.setItem('omsTimeTableDateFilter', moment(v).toString())}
            />
            <Box onClick={() => setPendingField('date', moment(pending.date).add(1, 'd').format())}
              sx={{ px: 1, height: '100%', display: 'flex', alignItems: 'center', cursor: 'pointer', '&:hover': { backgroundColor: '#f0f0f0' } }}>
              <ArrowForwardIosIcon sx={{ fontSize: '14px', color: 'rgba(0,0,0,0.54)' }} />
            </Box>
          </Box>

          {/* Service */}
          <FilterTextField
            select
            label="Service"
            size="small"
            variant="outlined"
            value={pending.serviceId}
            onChange={(e: any) => setPendingField('serviceId', e.target.value)}
            SelectProps={{
              multiple: true,
              renderValue: (sel: any) => sel.length ? `${sel.length} selected` : '',
            }}
            sx={{ minWidth: 140 }}
          >
              {Object.keys(ServiceOfferingEnums).map((key, i) => (
                <MenuItem key={key} value={i + 1}>{key}</MenuItem>
              ))}
          </FilterTextField>

          {/* Cook Type */}
          <FilterTextField
            select
            label="Cook Type"
            size="small"
            variant="outlined"
            value={pending.cookType}
            onChange={(e: any) => setPendingField('cookType', e.target.value)}
            sx={{ minWidth: 150 }}
          >
              <MenuItem value="">Show All</MenuItem>
              <MenuItem value={CookTypeFilter.MG_COOK}>MG_COOK</MenuItem>
              <MenuItem value={CookTypeFilter.PART_TIME}>PART_TIME</MenuItem>
              <MenuItem value={CookTypeFilter.ACTIVE_PART_TIME}>ACTIVE_PART_TIME</MenuItem>
          </FilterTextField>

          {/* Cook Type List — replaces the old MG Tag filter, whose values are now part of it */}
          <FilterTextField
            select
            label="Cook Type List"
            size="small"
            variant="outlined"
            value={pending.cookTypeList}
            onChange={(e: any) => setPendingField('cookTypeList', e.target.value)}
            SelectProps={{
              multiple: true,
              renderValue: (selected: any) => selected.length ? `${selected.length} selected` : '',
            }}
            sx={{ minWidth: 170 }}
          >
              {Object.values(CookServiceTypeEnum).map((serviceType) => (
                <MenuItem key={serviceType} value={serviceType}>{CookServiceTypeLabels[serviceType]}</MenuItem>
              ))}
          </FilterTextField>

          {/* Multi-Cook selector */}
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 240 }}
            options={cookSearchResults}
            getOptionLabel={(o: any) => o.label}
            isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
            value={pending.selectedCooks}
            onChange={(_, selected: any[]) => {
              setPendingField('selectedCooks', selected);
              setPendingField('cookIds', selected.map((c: any) => c.id));
            }}
            inputValue={cookSearchInput}
            onInputChange={(_, v) => setCookSearchInput(v)}
            renderInput={(params) => <TextField {...params} label="Filter by Cooks" placeholder="Search by name..." variant="outlined" size="small" sx={autocompleteInputSx} />}
            renderTags={(value, getTagProps) =>
              value.map((option: any, index) => (
                <Chip label={option.label} size="small" {...getTagProps({ index })} />
              ))
            }
            noOptionsText={cookSearchInput.length < 2 ? 'Type to search cooks' : 'No cooks found'}
          />
          </FilterContainer>
        </FilterWrapper>

        {/* ── Applied filter chips ── */}
        {(applied.city || applied.areaId || applied.serviceId.length > 0 || applied.cookType || applied.cookTypeList.length > 0 || applied.cookIds.length > 0) && (
          <Box sx={{ mx: '15px', mb: '8px', display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.5 }}>Active:</Typography>
            {applied.city && <Chip size="small" label={`City: ${cityList.find(c => c.id === applied.city)?.name || applied.city}`} color="primary" variant="outlined" />}
            {applied.areaId && <Chip size="small" label={`Area: ${applied.area?.label || applied.areaId}`} color="primary" variant="outlined" />}
            {applied.serviceId.length > 0 && <Chip size="small" label={`Services: ${applied.serviceId.length}`} color="primary" variant="outlined" />}
            {applied.cookType && <Chip size="small" label={`Cook Type: ${applied.cookType}`} color="primary" variant="outlined" />}
            {applied.cookTypeList.length > 0 && <Chip size="small" label={`Cook Types: ${applied.cookTypeList.length}`} color="primary" variant="outlined" />}
            {applied.selectedCooks.map((c: any) => <Chip key={c.id} size="small" label={c.label} color="secondary" variant="outlined" />)}
          </Box>
        )}

        {/* ── Calendar Grid ── */}
        <div style={{ display: 'flex' }}>
          {/* Pending OMS / Trials column */}
          <div style={{ textAlign: 'center', width: '200px' }} key="0" ref={(el) => (calendarRefs.current['0'] = el)} className="first-calendar">
            <Box fontWeight={700} fontSize={13} padding="6px" marginLeft="30px">Pending OMS / Trials</Box>
            <Calendar
              localizer={localizer}
              events={cookOmsMapping[0]}
              defaultView="day"
              views={['day']}
              date={appliedDate}
              style={{ height: '100%', backgroundColor: 'white' }}
              components={{ toolbar: CustomToolbar }}
              step={60}
              timeslots={1}
              onSelectEvent={onEventClick}
              eventPropGetter={eventStyleGetter}
              min={new Date(2024, 0, 1, 5, 0)}
              max={new Date(2024, 0, 1, 23, 59)}
            />
          </div>

          {isAnyLoading
            ? <Box display="flex" justifyContent="center" alignItems="center" width="100vw"><CircularLoader /></Box>
            : (
              <div style={{ display: 'flex', height: '100%', overflow: 'scroll' }}>
                {Object.keys(cookOmsMapping).length !== 0 &&
                  Object.entries(cookOmsMapping).map(([key, value]: any) =>
                    key !== '0' ? (
                      <div style={{ textAlign: 'center', width: '200px' }} key={key} ref={(el) => (calendarRefs.current[key] = el)}>
                        <Button
                          sx={{ borderRadius: 30, fontSize: '13px', ml: 2, position: 'sticky' }}
                          size="small"
                          component={Link}
                          href={`/#/Cooks/${value[0]?.cookId}/show`}
                        >
                          {value[0]?.heading}
                        </Button>
                        <Calendar
                          localizer={localizer}
                          events={value}
                          defaultView="day"
                          views={['day']}
                          date={appliedDate}
                          style={{ height: '100%', backgroundColor: 'white' }}
                          components={{ toolbar: CustomToolbar }}
                          step={60}
                          timeslots={1}
                          onSelectEvent={onEventClick}
                          eventPropGetter={eventStyleGetter}
                          min={new Date(2024, 0, 1, 5, 0)}
                          max={new Date(2024, 0, 1, 23, 59)}
                        />
                      </div>
                    ) : null
                  )}
              </div>
            )}
        </div>
      </div>

      {/* ── Legend ── */}
      <Box
        position="sticky"
        bottom={0}
        zIndex={1000}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: '28px',
          rowGap: '10px',
          backgroundColor: '#fff',
          borderTop: '1px solid rgb(225, 231, 239)',
          padding: '14px 20px',
        }}
      >
        {legends.map((legend) => (
          <Box key={legend.label} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: legend.color,
                borderRadius: '4px',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                flexShrink: 0,
              }}
            />
            <Typography fontSize={12} sx={{ whiteSpace: 'nowrap' }}>{legend.label}</Typography>
          </Box>
        ))}
      </Box>
    </Authenticated>
  );
};

export default CookServeCalendar;
